import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  User,
  Building,
  Save,
  Loader2
} from "lucide-react";

const Profile = () => {
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    company_name: "",
    industry: "fashion",
    gst_number: ""
  });

  useEffect(() => {
    fetchCompany();
  }, []);

  const fetchCompany = async () => {
    try {
      const response = await axios.get(`${API}/user/company`, { withCredentials: true });
      setCompany(response.data);
      setFormData({
        company_name: response.data.company_name || "",
        industry: response.data.industry || "fashion",
        gst_number: response.data.gst_number || ""
      });
    } catch (error) {
      console.error("Error fetching company:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/company`, formData, { withCredentials: true });
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="profile-page" className="max-w-2xl space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-neutral-900">
            Profile Settings
          </h1>
          <p className="text-neutral-600 mt-1">
            Manage your account and company information
          </p>
        </div>

        {/* User Info */}
        <Card className="border-neutral-100">
          <CardHeader>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-600" />
              Account Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <img 
                src={user?.picture || `https://ui-avatars.com/api/?name=${user?.name}&background=4338ca&color=fff`}
                alt={user?.name}
                className="w-16 h-16 rounded-full"
              />
              <div>
                <p className="font-semibold text-neutral-900">{user?.name}</p>
                <p className="text-sm text-neutral-500">{user?.email}</p>
                <p className="text-xs text-indigo-600 capitalize mt-1">{user?.role}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Company Info */}
        <Card className="border-neutral-100">
          <CardHeader>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <Building className="w-5 h-5 text-indigo-600" />
              Company Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="company_name">Company Name</Label>
              <Input 
                id="company_name"
                data-testid="company-name-input"
                value={formData.company_name}
                onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="Your company name"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Industry</Label>
              <div className="flex gap-4 mt-2">
                {["fashion", "cosmetic", "both"].map((ind) => (
                  <Button
                    key={ind}
                    type="button"
                    variant={formData.industry === ind ? "default" : "outline"}
                    onClick={() => setFormData(prev => ({ ...prev, industry: ind }))}
                    className={formData.industry === ind ? "bg-indigo-600" : ""}
                  >
                    {ind.charAt(0).toUpperCase() + ind.slice(1)}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="gst_number">GST Number (Optional)</Label>
              <Input 
                id="gst_number"
                data-testid="gst-number-input"
                value={formData.gst_number}
                onChange={(e) => setFormData(prev => ({ ...prev, gst_number: e.target.value }))}
                placeholder="e.g., 22AAAAA0000A1Z5"
                className="mt-1"
              />
            </div>

            <Button 
              data-testid="save-profile-btn"
              onClick={handleSave}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Profile;
